import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { NotificationCountProvider } from "./hooks/NotificationCountContext";
import LanguageSelectorScreen from "./i18n/LanguageSelectorScreen";
import AgeGate from "./components/AgeGate";
import Navbar from "./components/Navbar";
import CurrencyConverter from "./components/CurrencyConverter";
import useFCM, { syncPrefsWithSW, getNotifPrefs } from "./hooks/useFCM";
import NotificationPrompt from "./components/NotificationPrompt";
import { syncWatchedFromFirebase } from "./utils/watchedPackages";
import "./App.css";

// ── Critical path: loaded eagerly (needed on first paint) ────────────────
// These are small components needed immediately, so no lazy loading
import CookieConsent from "./components/CookieConsent";

// ── Lazy load all pages — reduces initial JS bundle by ~60-70% ───────────
// Each page is code-split into its own chunk, loaded only when navigated to.
const Home           = lazy(() => import("./pages/Home"));
const Payment        = lazy(() => import("./pages/Payment"));
const AdminPanelWrapper = lazy(() => import("./pages/AdminPanelWrapper"));
const AdminHub       = lazy(() => import("./pages/AdminHub"));
const BrokenPackages = lazy(() => import("./pages/BrokenPackages"));
const PendingPaymentsWrapper = lazy(() => import("./pages/PendingPaymentsWrapper"));
const AnalyticsPage  = lazy(() => import("./pages/AnalyticsPage"));
const Support        = lazy(() => import("./pages/Support"));
const SpecialRequest = lazy(() => import("./pages/SpecialRequest"));
const WaitingPage    = lazy(() => import("./pages/WaitingPage"));
const DownloadPage   = lazy(() => import("./pages/DownloadPage"));
const VerifyPage     = lazy(() => import("./pages/VerifyPage"));
const RewardPage     = lazy(() => import("./pages/RewardPage"));
const LinkequipPage  = lazy(() => import("./pages/LinkequipPage"));
const EducationalVideo = lazy(() => import("./pages/EducationalVideo"));
const Supportus      = lazy(() => import("./pages/Supportus"));
const FirebaseActionPage = lazy(() => import("./pages/FirebaseActionPage"));
const LoginPage      = lazy(() => import("./pages/LoginPage"));
const SignupPage     = lazy(() => import("./pages/SignupPage"));
const SignupSuccessPage = lazy(() => import("./pages/SignupSuccessPage"));
const UsernamePage   = lazy(() => import("./pages/UsernamePage"));
const EditProfile    = lazy(() => import("./pages/EditProfile"));
const ViewProfile    = lazy(() => import("./pages/ViewProfile"));
const AiSupport      = lazy(() => import("./pages/AiSupport"));
const Customizenotifications = lazy(() => import("./pages/Customizenotifications"));
const SendComplaint  = lazy(() => import("./pages/SendComplaint"));
const Mailbox        = lazy(() => import("./pages/Mailbox"));
const RespondingComplaints = lazy(() => import("./pages/RespondingComplaints"));
const AdminViewProfile     = lazy(() => import("./pages/AdminViewProfile"));
const ChatsPage            = lazy(() => import("./pages/ChatsPage"));
const FriendsPage          = lazy(() => import("./pages/FriendsPage"));
const PrivateChatPage      = lazy(() => import("./pages/PrivateChatPage"));
const SettingsPage         = lazy(() => import("./pages/SettingsPage"));
const PleaseSignup         = lazy(() => import("./pages/PleaseSignup"));
const PackageView          = lazy(() => import("./pages/PackageView"));
const PrivacyPolicyPage    = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage   = lazy(() => import("./pages/TermsOfServicePage"));

// ── Minimal loading fallback — no spinner, just background ───────────────
// Using null prevents a flash of content when navigating between pages
const PageLoader = () => (
  <div style={{
    position: "fixed", inset: 0,
    background: "#080000",
    display: "flex", alignItems: "center", justifyContent: "center",
    gap: 10, zIndex: 50
  }}>
    <div className="auth-loading-dot" />
    <div className="auth-loading-dot" />
    <div className="auth-loading-dot" />
  </div>
);

// ── ScrollToTop: يضمن أن كل صفحة تبدأ من أعلى بشكل مستقل — حل مشكلة الـ Scroll المشترك ──────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// ── Layout for regular pages (with Navbar) ──────────────────────────────────

function MainLayout({ children, onCurrencyToggle, currencyOpen, onCurrencyClose }) {
  return (
    <div className="app-root">
      <ScrollToTop />
      <Navbar onCurrencyToggle={onCurrencyToggle} />
      <CurrencyConverter
        isOpen={currencyOpen}
        onClose={onCurrencyClose}
      />
      {children}
    </div>
  );
}

// ── Layout for bypass pages (NO Navbar, clean full-screen) ──────────────────
function BypassLayout({ children }) {
  return (
    <div className="bypass-app-root">
      {children}
    </div>
  );
}

// ── Protected Route: requires auth completed (username chosen) ───────────────
function ProtectedRoute({ children }) {
  const { user, authCompleted, authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="auth-loading-screen">
        <div className="auth-loading-dot" />
        <div className="auth-loading-dot" />
        <div className="auth-loading-dot" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!authCompleted) {
    return <Navigate to="/username" replace />;
  }

  return children;
}

// ── Auth Route: redirect fully-authenticated users away from login ────────────
function AuthRoute({ children }) {
  const { user, authCompleted, authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="auth-loading-screen">
        <div className="auth-loading-dot" />
        <div className="auth-loading-dot" />
        <div className="auth-loading-dot" />
      </div>
    );
  }

  if (user && authCompleted) {
    return <Navigate to="/" replace />;
  }

  if (user && !authCompleted) {
    return <Navigate to="/username" replace />;
  }

  return children;
}

// ── Username Route: only for logged-in users who haven't picked name yet ─────
function UsernameRoute({ children }) {
  const { user, authCompleted, authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="auth-loading-screen">
        <div className="auth-loading-dot" />
        <div className="auth-loading-dot" />
        <div className="auth-loading-dot" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (authCompleted) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// ── Main App Content ─────────────────────────────────────────────────────────
// ── Global FCM Listener: يستمع لإشعارات RTDB الواردة ─────────────────────────
// يعمل للزوار والأعضاء على حد سواء
// ملاحظة: معالجة الإشعارات تتم بالكامل داخل useFCM.js عبر startRTDBNotifListener
// الذي يستخدم resolveNotifText للنصوص ثنائية اللغة ويتجنب الإشعارات الفارغة
function GlobalFCMListener() {
  const { user } = useAuth();

  // تسجيل الجهاز واستقبال إشعارات المقدمة + RTDB listener (يدعم الزوار الآن)
  // startRTDBNotifListener مدمج داخل useFCM ويستخدم resolveNotifText بشكل صحيح
  useFCM(user);

  // مزامنة تفضيلات الإشعارات مع الـ Service Worker عند أول تحميل
  useEffect(() => {
    syncPrefsWithSW(getNotifPrefs());
  }, []);

  // ── مزامنة الحزم المُشاهدة من Firebase للأعضاء المسجلين ─────────────────
  // عند تسجيل الدخول من متصفح/جهاز جديد، نسحب قائمة "watchedPackages"
  // من profiles/{uid} وندمجها مع localStorage حتى تظل علامة "تمت المشاهدة"
  // ظاهرة على نفس الحزم. الزوار يعتمدون على localStorage فقط.
  useEffect(() => {
    if (!user || user.isAnonymous) return;
    // fire-and-forget — UI updates عبر "packagesUpdated" event من الـ util
    syncWatchedFromFirebase();
  }, [user]);

  return null;
}

function AppContent() {
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const navigate = useNavigate();
  const { hasChosen, lang } = useLanguage();
  const { user } = useAuth();
  const isAr = lang === "ar";

  return (
    <AgeGate>
      {!hasChosen && <LanguageSelectorScreen />}
      {hasChosen && <CookieConsent />}
      {/* ── مستمع إشعارات FCM العالمي ── */}
      <GlobalFCMListener />
      {/* ── رسالة تشجيع تفعيل الإشعارات ── */}
      {hasChosen && <NotificationPrompt currentUser={user} isAr={isAr} />}

      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ── Auth Pages — NO Navbar, full-screen ── */}
          <Route
            path="/login"
            element={
              <AuthRoute>
                <BypassLayout><LoginPage /></BypassLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/signup"
            element={
              <BypassLayout><SignupPage /></BypassLayout>
            }
          />

          <Route
            path="/username"
            element={
              <UsernameRoute>
                <BypassLayout><UsernamePage /></BypassLayout>
              </UsernameRoute>
            }
          />

          <Route
            path="/signup/Successcreatedaccount"
            element={
              <BypassLayout><SignupSuccessPage /></BypassLayout>
            }
          />

          {/* ── Bypass flow pages — NO Navbar ── */}
          <Route
            path="/waiting"
            element={
              <ProtectedRoute>
                <BypassLayout><WaitingPage /></BypassLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/waiting/download"
            element={
              <ProtectedRoute>
                <BypassLayout><DownloadPage /></BypassLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/waiting/download/verify"
            element={
              <ProtectedRoute>
                <BypassLayout><VerifyPage /></BypassLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/waiting/download/verify/reward"
            element={
              <ProtectedRoute>
                <BypassLayout><RewardPage /></BypassLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/waiting/download/verify/reward/linkequip"
            element={
              <ProtectedRoute>
                <BypassLayout><LinkequipPage /></BypassLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Regular pages — WITH Navbar ── */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <Home />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <Payment onBack={() => navigate("/")} />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/adminpanel"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <AdminPanelWrapper />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/adminpanel/analytics"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <AnalyticsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/adminpanel/pendingpayments"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <PendingPaymentsWrapper />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <Support />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/specialrequest"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <SpecialRequest />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Educationalvideo"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <EducationalVideo />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/Supportus"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <Supportus />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/EditProfile"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <EditProfile />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ViewProfile"
            element={
              <MainLayout
                onCurrencyToggle={() => setCurrencyOpen(true)}
                currencyOpen={currencyOpen}
                onCurrencyClose={() => setCurrencyOpen(false)}
              >
                <ViewProfile />
              </MainLayout>
            }
          />

          {/* ── Firebase Auth Action Page — NO Navbar ── */}
          <Route
            path="/auth/action"
            element={
              <BypassLayout>
                <FirebaseActionPage />
              </BypassLayout>
            }
          />

          {/* ── Send Complaint ── */}
          <Route
            path="/send-complaint"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <SendComplaint />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Mailbox (complaints & support) ── */}
          <Route
            path="/mailbox/:type"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <Mailbox />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/mailbox"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <Mailbox />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Admin: Hub (مركز التحكم الموحد) ── */}
          <Route
            path="/adminpanel/hub"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <AdminHub />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Admin: Broken Packages (الحزم التالفة) ── */}
          <Route
            path="/adminpanel/broken-packages"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <BrokenPackages />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Admin: Responding Complaints ── */}
          <Route
            path="/adminpanel/Respondingcomplaints"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <RespondingComplaints />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Admin: View Profile from Complaints/Support ── */}
          <Route
            path="/adminpanel/Respondingcomplaints/ViewProfile/:userId"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <AdminViewProfile />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Chat System — نظام مستقل بدون Navbar الموقع ── */}
          <Route
            path="/chats"
            element={<BypassLayout><ChatsPage /></BypassLayout>}
          />
          <Route
            path="/chats/public"
            element={<BypassLayout><ChatsPage /></BypassLayout>}
          />
          <Route
            path="/friends"
            element={<BypassLayout><FriendsPage /></BypassLayout>}
          />
          <Route
            path="/privatechat"
            element={<BypassLayout><PrivateChatPage /></BypassLayout>}
          />
          <Route
            path="/privatechat/:uid"
            element={<BypassLayout><PrivateChatPage /></BypassLayout>}
          />

          {/* ── ViewProfile/:uid — عرض ملف مستخدم آخر (متاح للجميع بما فيهم الزوار) ── */}
          <Route
            path="/ViewProfile/:uid"
            element={
              <MainLayout
                onCurrencyToggle={() => setCurrencyOpen(true)}
                currencyOpen={currencyOpen}
                onCurrencyClose={() => setCurrencyOpen(false)}
              >
                <ViewProfile />
              </MainLayout>
            }
          />

          {/* ── AI Support Page ── */}
          <Route
            path="/AiSupport"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <AiSupport />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Customize Notifications Page ── */}
          <Route
            path="/Customizenotifications"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <Customizenotifications />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Settings Page — صفحة الإعدادات المستقلة ── */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <MainLayout
                  onCurrencyToggle={() => setCurrencyOpen(true)}
                  currencyOpen={currencyOpen}
                  onCurrencyClose={() => setCurrencyOpen(false)}
                >
                  <SettingsPage onCurrencyToggle={() => setCurrencyOpen(true)} />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Please Signup — صفحة تشجيع الزوار على التسجيل ── */}
          <Route
            path="/pleasesignup"
            element={
              <BypassLayout><PleaseSignup /></BypassLayout>
            }
          />

          {/* ── Privacy Policy ── */}
          <Route
            path="/privacy-policy"
            element={
              <MainLayout
                onCurrencyToggle={() => setCurrencyOpen(true)}
                currencyOpen={currencyOpen}
                onCurrencyClose={() => setCurrencyOpen(false)}
              >
                <PrivacyPolicyPage />
              </MainLayout>
            }
          />

          {/* ── Terms of Service ── */}
          <Route
            path="/terms-of-service"
            element={
              <MainLayout
                onCurrencyToggle={() => setCurrencyOpen(true)}
                currencyOpen={currencyOpen}
                onCurrencyClose={() => setCurrencyOpen(false)}
              >
                <TermsOfServicePage />
              </MainLayout>
            }
          />

          {/* ── Package share-link landing page ─────────────────────────
              URL format:  /{shortId}?ref=REF-{refId}
              e.g.  /b9l58r?ref=REF-3zfq883rxc
              The PackageView component resolves shortId+refId against
              the live performers list and renders a professional share
              page. Placed BEFORE the wildcard fallback so it intercepts
              short-id URLs while leaving every named route untouched
              (none of which collides with a 6-char lowercase slug). ─── */}
          <Route
            path="/:shortId"
            element={
              <BypassLayout><PackageView /></BypassLayout>
            }
          />

          {/* ── Fallback: redirect unknown routes to home ── */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AgeGate>
  );
}

// ── Wrapper داخلي يحتاج AuthContext ─────────────────────────────────────────
function AppWithNotifCounts() {
  const { user } = useAuth();
  return (
    <NotificationCountProvider currentUser={user}>
      <AppContent />
      <Analytics />
      <SpeedInsights />
    </NotificationCountProvider>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <AppWithNotifCounts />
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
